/* eslint-disable sort-keys */
import { Clickable, H3, H4, IconLockup, P, Strong } from '@ahmdigital/ui';
import getOpeningHours from '@ahmdigital/logic/lib/utils/get-opening-hours';
import styled from 'styled-components';

import { LIFE_INSURANCE } from '../../../../models/life-insurance/constants';
import constants from '../../../../ahm-constants';
import PreLineParagraph from '../../components/pre-line-paragraph';

const StyledDiv = styled.div`
  margin-top: 3rem;
`;

const getLifeAndIncomeContent = () => {
  const openingHoursKey = constants.AHM_SERVICE_NAME.LIFE_INSURANCE_ZURICH;

  return {
    meta: {
      title: 'ahm Life Insurance and Income Protection - Contact Us',
      description:
        'Have questions about ahm insurance policies or quotes? Contact us via phone or email today for help with ahm life and income protection insurance queries.',
    },
    banner: {
      title: 'Contact us',
      preHeading: 'ahm Life Insurance and Income Protection Insurance',
    },
    body: (
      <>
        <StyledDiv>
          <H3>New customers</H3>
          <IconLockup icon={{ name: 'phone-circle' }}>
            <P looksLike="h4">
              <Clickable
                to={`tel:
              ${LIFE_INSURANCE.NEOS.CONTACT.PHONE.SALES.STANDARD}`}
              >
                {LIFE_INSURANCE.NEOS.CONTACT.PHONE.SALES.PRETTY}
              </Clickable>
            </P>
            <PreLineParagraph>{getOpeningHours(openingHoursKey)}</PreLineParagraph>
          </IconLockup>
        </StyledDiv>
        <StyledDiv>
          <H3>Existing customers</H3>
          <P>If your policy starts with ‘AL’ or ‘AZ’ and you have any customer queries including how to claim:</P>
          <IconLockup icon={{ name: 'phone-circle' }}>
            <P looksLike="h4">
              <Clickable to={`tel:${LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.STANDARD}`}>
                {LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.PRETTY}
              </Clickable>
            </P>
            <PreLineParagraph>{getOpeningHours(openingHoursKey)}</PreLineParagraph>
          </IconLockup>
          <br />
          <IconLockup icon={{ name: 'device-desktop' }}>
            <P looksLike="h4">Email</P>
            <P>
              <Clickable to={`mailto:${LIFE_INSURANCE.NEOS.CONTACT.EMAIL}`}>
                {LIFE_INSURANCE.NEOS.CONTACT.EMAIL}
              </Clickable>
            </P>
          </IconLockup>
          <br />

          <>
            <br />
            <H4>Need to make a claim?</H4>
            <P>
              <Clickable to={constants.URLS.SALES.LIFE_INSURANCE_CLAIM}>Click here</Clickable> to find out how to lodge
              your claim online 24/7.
            </P>
            <H4>Make a complaint (for policy numbers starting with ‘AL’ or ‘AZ’)</H4>
            <P>
              We hope that you never have a reason to complain, but if you do, we will do our best to work with you to
              resolve it. To lodge a complaint or if you require assistance to lodge a complaint, please contact us in
              the first instance by phone, email or in writing.
            </P>
            <P>
              <Strong>Call us:</Strong>{' '}
              <Clickable to={`tel:${LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.STANDARD}`}>
                {LIFE_INSURANCE.NEOS.CONTACT.PHONE.CLAIMS.PRETTY}
              </Clickable>
              <br />
              <Strong>Email us:</Strong>{' '}
              <Clickable to={`mailto:${LIFE_INSURANCE.NEOS.CONTACT.EMAIL}`}>
                {LIFE_INSURANCE.NEOS.CONTACT.EMAIL}
              </Clickable>{' '}
              <br />
              <Strong>Write to us:</Strong> AHM Life Insurance, GPO Box 239, Sydney NSW 2001
            </P>
            <P>
              We’ll endeavour to manage your complaint to your satisfaction without undue delay and we commit to
              acknowledging any complaint within one business day. There is no charge for lodging a complaint.
            </P>
            <P>
              If you feel we haven’t resolved your complaint to your satisfaction, you can lodge your complaint with the
              Australian Financial Complaints Authority (AFCA). AFCA is an independent organisation, which providers
              free assistance to customers who have complaints about financial products and services. AFCA can be
              contacted at:
            </P>
            <P>
              <Strong>Phone:</Strong> <Clickable to="tel:1800931678">1800 931 678</Clickable>
              <br />
              <Strong>Website:</Strong>{' '}
              <Clickable to={constants.URLS.THIRD_PARTY.AUSTRALIAN_FINANCIAL_COMPLAINTS_AUTHORITY}>
                www.afca.org.au
              </Clickable>
              <br />
              <Strong>Email:</Strong> <Clickable to="mailto:info@afca.org.au">info@afca.org.au</Clickable>
              <br />
              <Strong>Address:</Strong> GPO Box 3, Melbourne VIC 3001
              <br />
            </P>
            <P>
              Please click <Clickable to={constants.URLS.THIRD_PARTY.NEOS_FURTHER_COMPLAINTS}>here</Clickable> for the
              Complaints Management Policy.
            </P>
          </>
        </StyledDiv>
      </>
    ),
    thingsYouShouldKnow: (
      <>
        <P>
          Any advice provided is general only and doesn’t consider your objectives, financial situation or needs. You
          should carefully read the relevant{' '}
          <Clickable to={constants.URLS.SALES.LIFE_INSURANCE_USEFUL_DOCUMENTS}>Product Disclosure Statement</Clickable>{' '}
          (which sets out the product terms, conditions and exclusions),{' '}
          <Clickable to={constants.URLS.SALES.LIFE_INSURANCE_USEFUL_DOCUMENTS}> Target Market Determination</Clickable>{' '}
          and <Clickable to={constants.URLS.SALES.LIFE_INSURANCE_USEFUL_DOCUMENTS}> Financial Services Guide</Clickable>{' '}
          to ensure the product is right for you before making a decision to acquire, or continue to hold, the product.
          ahm Life Insurance and ahm Income Protection (collectively ahm life insurance) are issued by the insurer,
          Integrity Life Australia Limited ABN 83 089 981 073, AFSL 245492 (Integrity Life) administered and distributed
          by NEOS Direct, a registered business name of NDLI Pty Ltd ABN 70 665 747 277 AFSL 547119, and promoted by ahm
          health insurance (ahm), a business of Medibank Private Limited ABN 47 080 890 259. Medibank Private Limited is
          a Corporate Authorised Representative 286089 of NEOS Direct.
        </P>
        <P>
          Your personal information will be handled in accordance with the Privacy Policies of the parties involved.
          Click here for{' '}
          <Clickable to={constants.URLS.THIRD_PARTY.NEOS_PRIVACY_POLICY}>NEOS Direct’s Privacy Policy</Clickable>,{' '}
          <Clickable to={constants.URLS.SALES.PRIVACY}>ahm’s Privacy Policy</Clickable> or{' '}
          <Clickable to={constants.URLS.THIRD_PARTY.INTEGRITY_LIFE_PRIVACY_POLICY}>
            Integrity Life’s Privacy Policy
          </Clickable>
          .
        </P>
      </>
    ),
  };
};

export default getLifeAndIncomeContent;
